import { ChangeDetectionStrategy, Component, Inject, Input, OnInit } from '@angular/core';
import { FeatureFlag } from '@app/infrastructure/feature-flag-manager/feature-flag-decorator';
import { FEATURE_FLAGS } from '@app/infrastructure/feature-flag-manager/feature-flag-list';
import { FeatureFlagManagerService } from '@app/infrastructure/feature-flag-manager/feature-flag-manager.service';
import { Organization } from '@app/interfaces/accounts_list';
import { AnalyticsEvents, AnalyticStatus, MixpanelService } from '@app/services/mixpanel.service';
import { PresenterModeService } from '@app/services/presenter-mode.service';
import { UserService } from '@app/services/user/user.service';
import { SASTRIFY_ORGANIZATIONS_LIST } from '@app/sidebar-layout/components/header/header.config';
import { ISection } from '@app/sidebar-layout/interfaces/i-section';
import { ISidebarRestrictions } from '@app/sidebar-layout/interfaces/i-sidebar-restrictions';
import { AppRouteHelper } from '@shared/helpers/app-route.helper';
import { APP_CONFIG, AppConfiguration } from '@app/infrastructure/auth/app-configuration';

@Component({
	selector: 'app-header',
	templateUrl: './header.component.html',
	styleUrls: ['./header.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
	@FeatureFlag(FEATURE_FLAGS.ENABLE_OPTIMIZATION_HUB) enableOptimizationHub: boolean;

	@Input()
	public set restrictions(restrictions: ISidebarRestrictions) {
		this._restrictions = restrictions;
	}

	@Input()
	public set organizations(organizations: Organization[]) {
		this._organizations = organizations;
	}

	@Input()
	public set sections(sections: ISection[]) {
		this._sections = sections;
	}

	@Input()
	public set section(section: ISection) {
		this._section = section;
	}

	public readonly appRouteHelper = AppRouteHelper;
	public readonly componentsOverviewRoute = `/${AppRouteHelper.componentsOverview}`;
	public readonly isComponentsOverviewAvailable = this.appConfig.includeComponentsOverview;

	private _restrictions: ISidebarRestrictions = null;
	private _organizations: Organization[];
	private _organizationId: string;
	private _sections: ISection[];
	private _section: ISection;
	private _userName: string;
	private _userRole: string;
	private readonly SASTRIFY_ORGANIZATIONS_LIST: string[] = SASTRIFY_ORGANIZATIONS_LIST.map((item) => item.id);

	constructor(
		@Inject(APP_CONFIG) private appConfig: AppConfiguration,
		private readonly presenterMode: PresenterModeService,
		private readonly userService: UserService,
		public featureFlagManagerService: FeatureFlagManagerService,
		private mixpanelService: MixpanelService,
	) {}

	public ngOnInit(): void {
		this._organizationId = this.userService.organizationId;
		this._userName = this.userService.userNickname;
		this._userRole = this.userService.userRole;
	}

	public togglePresentMode(): void {
		this.presenterMode.toggleMode();
	}

	public logout(): void {
		try {
			if (this.enableOptimizationHub) {
				this.mixpanelService.track(AnalyticsEvents.sessionOver, { Status: AnalyticStatus.logout });
			}
		} catch (error) {
			console.log('Mixpanel tracking failed:', error);
		}
		this.userService.logout();
	}

	public get isShowSastrify(): boolean {
		return this.SASTRIFY_ORGANIZATIONS_LIST.includes(this.organizationId);
	}

	public get isOrganizationPickerAllowed(): boolean {
		return this._restrictions.isOrganizationPickerAllowed;
	}

	public get isPresentationModeAllowed(): boolean {
		return this._restrictions.isPresentationModeAllowed;
	}

	public get isNotificationsAllowed(): boolean {
		return (
			this._restrictions.isNotificationsAllowed &&
			(!this.appConfig.production ||
				(globalThis.location.href?.includes('app.zesty.co') &&
					!this.appConfig.partner.id &&
					!this.userService.organization.reseller_managed))
		);
	}

	public get organizationId(): string {
		return this._organizationId;
	}

	public get organizations(): Organization[] {
		return this._organizations;
	}

	public get sections(): ISection[] {
		return this._sections;
	}

	public get userName(): string {
		return this._userName;
	}

	public get userRole(): string {
		return this._userRole;
	}
}
